import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { LoadingButton } from '@/components/LoadingButton';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Zap, AlertCircle, Square } from 'lucide-react';
import { toast } from 'sonner';
import { getGetProfilesByIdQueryKey } from '@/api/control/control-plane/control-plane';
import type { ScheduleDto } from '@/api/control/missionImpactAPI.schemas';
import { usePostProfileBurst } from '@/api/burst-control';
import { useLanguage } from '@/contexts/LanguageContext';

/** The maximum burst window in hours (kept in sync with the control-plane cap). */
const MAX_BURST_WINDOW_HOURS = 8;

/** The default burst window in hours pre-filled in the activation form. */
const DEFAULT_BURST_WINDOW_HOURS = 4;

interface BurstDialogProps {
  orgId: string;
  profileId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The profile's current burst expiry (ISO-8601 UTC), or null when no burst is active. */
  burstExpiresUtc: string | null;
  /** The profile's quiet-time schedules, used to warn when a burst window overlaps quiet time. */
  schedules: ScheduleDto[];
}

/** Parses an "HH:MM[:SS]" time string into minutes since midnight. */
function parseTimeToMinutes(time: string): number {
  const [h, m] = time.split(':');
  return parseInt(h, 10) * 60 + parseInt(m || '0', 10);
}

/**
 * Returns true when a burst of the given window (starting now, in UTC) would overlap any quiet-time
 * schedule. Quiet-time windows are recurring UTC time-of-day ranges (daily, or weekly gated on the
 * window's start weekday); ranges whose end is at or before their start wrap past midnight. Because a
 * burst window is at most a few hours, it is enough to test each schedule's occurrences on the
 * calendar days the burst spans (plus the prior day, to catch a window that wraps into it).
 */
function burstOverlapsQuietTime(
  nowUtc: Date,
  windowHours: number,
  schedules: ScheduleDto[]
): boolean {
  if (windowHours <= 0 || schedules.length === 0) {
    return false;
  }

  const startMs = nowUtc.getTime();
  const endMs = startMs + windowHours * 60 * 60 * 1000;
  const dayMs = 24 * 60 * 60 * 1000;
  const lastOffset = Math.ceil(windowHours / 24) + 1;

  for (const schedule of schedules) {
    const startMinutes = parseTimeToMinutes(schedule.startTime);
    const endMinutes = parseTimeToMinutes(schedule.endTime);

    for (let offset = -1; offset <= lastOffset; offset++) {
      const dayStart = Date.UTC(
        nowUtc.getUTCFullYear(),
        nowUtc.getUTCMonth(),
        nowUtc.getUTCDate() + offset
      );
      const windowStart = dayStart + startMinutes * 60 * 1000;
      let windowEnd = dayStart + endMinutes * 60 * 1000;
      if (windowEnd <= windowStart) {
        windowEnd += dayMs; // wraps past midnight
      }

      if (windowStart < endMs && windowEnd > startMs) {
        // A weekly window recurs only on its start day's weekday.
        if (schedule.$type === 'QuietTimeWeekly') {
          const weekday = new Date(windowStart).getUTCDay();
          if (!schedule.days.map(Number).includes(weekday)) {
            continue;
          }
        }
        return true;
      }
    }
  }

  return false;
}

/**
 * Dialog for activating or clearing an accelerated sync burst on a profile.
 */
export function BurstDialog({
  orgId,
  profileId,
  open,
  onOpenChange,
  burstExpiresUtc,
  schedules,
}: BurstDialogProps) {
  const { t } = useLanguage();
  const queryClient = useQueryClient();
  const [hours, setHours] = useState(String(DEFAULT_BURST_WINDOW_HOURS));

  const isActive = burstExpiresUtc != null && new Date(burstExpiresUtc).getTime() > Date.now();

  const parsedHours = Number(hours);
  const isValidWindow =
    Number.isFinite(parsedHours) && parsedHours > 0 && parsedHours <= MAX_BURST_WINDOW_HOURS;
  const overlapsQuietTime =
    isValidWindow && burstOverlapsQuietTime(new Date(), parsedHours, schedules);

  const burstMutation = usePostProfileBurst({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: getGetProfilesByIdQueryKey(orgId, profileId),
        });
      },
    },
  });

  const handleActivate = () => {
    burstMutation.mutate(
      { orgId, profileId, data: { windowHours: parsedHours } },
      {
        onSuccess: () => {
          toast.success(t.profileDetail.burst.activateSuccess);
          onOpenChange(false);
        },
        onError: () => {
          toast.error(t.profileDetail.burst.activateError);
        },
      }
    );
  };

  const handleStop = () => {
    burstMutation.mutate(
      { orgId, profileId, data: { windowHours: null } },
      {
        onSuccess: () => {
          toast.success(t.profileDetail.burst.stopSuccess);
          onOpenChange(false);
        },
        onError: () => {
          toast.error(t.profileDetail.burst.stopError);
        },
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{t.profileDetail.burst.title}</DialogTitle>
          <DialogDescription>{t.profileDetail.burst.description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {isActive && (
            <Alert>
              <Zap className="h-4 w-4" />
              <AlertDescription>
                {t.profileDetail.burst.activeUntil.replace(
                  '{time}',
                  new Date(burstExpiresUtc).toLocaleString()
                )}
              </AlertDescription>
            </Alert>
          )}

          {/* While a burst is active the only action is to stop it; the activation form is hidden. */}
          {!isActive && (
            <>
              <div className="space-y-2">
                <Label htmlFor="burst-hours">{t.profileDetail.burst.windowLabel}</Label>
                <Input
                  id="burst-hours"
                  type="number"
                  min="1"
                  max={String(MAX_BURST_WINDOW_HOURS)}
                  value={hours}
                  onChange={(e) => {
                    setHours(e.target.value);
                  }}
                  className="w-24"
                />
                <p className="text-sm text-muted-foreground">
                  {t.profileDetail.burst.windowHelp.replace('{max}', String(MAX_BURST_WINDOW_HOURS))}
                </p>
              </div>

              <div className="min-h-[60px]">
                {overlapsQuietTime && (
                  <Alert>
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{t.profileDetail.burst.overlapWarning}</AlertDescription>
                  </Alert>
                )}
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => {
              onOpenChange(false);
            }}
            disabled={burstMutation.isPending}
          >
            {t.profileDetail.burst.cancel}
          </Button>
          {isActive ? (
            <LoadingButton
              variant="destructive"
              onClick={handleStop}
              isLoading={burstMutation.isPending}
            >
              <Square className="h-4 w-4" />
              {t.profileDetail.burst.stop}
            </LoadingButton>
          ) : (
            <LoadingButton
              onClick={handleActivate}
              disabled={!isValidWindow}
              isLoading={burstMutation.isPending}
            >
              <Zap className="h-4 w-4" />
              {t.profileDetail.burst.activate}
            </LoadingButton>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
